<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { PhBookOpen, PhListNumbers } from '@phosphor-icons/vue'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  useSidebar,
} from '@/components/ui/sidebar'
import DashboardSidebarBrowsePanels from '@/components/sidebar/DashboardSidebarBrowsePanels.vue'

const navigationModes = [
  { value: 'surah', label: 'Surah', icon: PhBookOpen },
  { value: 'juz', label: 'Juz', icon: PhListNumbers },
] as const

const props = defineProps<{
  quranState: {
    navigationMode: 'surah' | 'verse' | 'juz'
    selectedSurah: number
    selectedVerse: number
    selectedJuz: number
    selectedPage: number
    surahSearchQuery: string
    verseSurahSearchQuery: string
    verseNumber: number
    juzSearchQuery: string
  }
}>()

const emit = defineEmits<{
  'update:navigationMode': [value: 'surah' | 'verse' | 'juz']
  'update:selectedSurah': [value: number]
  'update:selectedVerse': [value: number]
  'update:selectedJuz': [value: number]
  'update:selectedPage': [value: number]
  'update:surahSearchQuery': [value: string]
  'update:verseSurahSearchQuery': [value: string]
  'update:verseNumber': [value: number]
  'update:juzSearchQuery': [value: string]
  'navigate-to': [data: { mode: 'surah' | 'verse' | 'juz'; value: number; surah?: number; verse?: number }]
}>()

const route = useRoute()
const { isMobile, open, openMobile } = useSidebar()

/** Mobile sheet uses openMobile; desktop rail uses open. */
const sidebarOpen = computed(() => (isMobile.value ? openMobile.value : open.value))

watch(sidebarOpen, (visible, wasVisible) => {
  if (route.name !== 'quran') return
  if (visible && wasVisible === false) {
    emit('update:navigationMode', 'surah')
  }
})

const quranState = computed(() => props.quranState)
</script>

<template>
  <Sidebar collapsible="icon" side="left">
    <SidebarContent class="flex-1 overflow-y-auto min-h-0 pt-14">
      <SidebarGroup>
        <div
          class="border-b border-border/70 bg-sidebar/95 px-3 py-2 backdrop-blur group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-2.5 sticky top-0 z-10"
        >
          <div
            class="grid min-w-0 w-full grid-cols-2 gap-2 rounded-2xl bg-moss-500/10 p-1.5 group-data-[collapsible=icon]:grid-cols-1 group-data-[collapsible=icon]:gap-2 group-data-[collapsible=icon]:px-1 group-data-[collapsible=icon]:py-2"
          >
            <button
              v-for="mode in navigationModes"
              :key="mode.value"
              type="button"
              :title="`Browse by ${mode.label}`"
              :aria-label="`Browse by ${mode.label}`"
              :class="[
                'flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-medium transition-all duration-200 sm:text-sm',
                'group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:h-12 group-data-[collapsible=icon]:w-12 group-data-[collapsible=icon]:shrink-0 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-0',
                quranState.navigationMode === mode.value
                  ? 'bg-[#046A38] text-bullion-50 shadow-lg shadow-moss-600/20'
                  : 'text-foreground/70 hover:bg-moss-500/20 hover:text-foreground',
              ]"
              @click="emit('update:navigationMode', mode.value)"
            >
              <component :is="mode.icon" :size="20" class="shrink-0" />
              <span class="group-data-[collapsible=icon]:hidden">{{ mode.label }}</span>
            </button>
          </div>
        </div>

        <SidebarGroupContent>
          <DashboardSidebarBrowsePanels
            :quran-state="quranState"
            @update:surah-search-query="emit('update:surahSearchQuery', $event)"
            @update:juz-search-query="emit('update:juzSearchQuery', $event)"
            @navigate-to="emit('navigate-to', $event)"
          />
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>
  </Sidebar>
</template>
